import React, { useEffect, useState } from "react";
import styles from "./Dashboard.module.scss";
import TotalUsersSVG from "components/SVGComponents/TotalUsersSVG";
import TotalBusinessSVG from "components/SVGComponents/TotalBusinessSVG";
import TotalCreativesSVG from "components/SVGComponents/TotalCreativesSVG";
import SubscribedBusinessSVG from "components/SVGComponents/SubscribedBusinessSVG";
import { combineClasses, formatLastSeen, serverFiles } from "utils/utils";
import FilterIconSVG from "components/SVGComponents/FilterIconSVG";
import Filter from "../SharedComponents/Filter/Filter";
import GrossVolumeChart from "./GrossVolumeChart/GrossVolumeChart";
import { useDispatch, useSelector } from "react-redux";
import {
  getMetrics,
  getOnlinePresence,
  getSignUpCounts,
} from "reducer/admin/dashboardSlice";

const Dashboard = () => {
  const [filterModal, setFilterModal] = useState(false);
  const dispatch = useDispatch();

  const { metrics, filters, onlinePresence } = useSelector(
    (state) => state.dashboard
  );

  const year = filters?.fromDate
    ? new Date(filters.fromDate).getFullYear()
    : new Date().getFullYear();

  const stats = [
    {
      title: "Total Users",
      icon: <TotalUsersSVG />,
    },
    {
      title: "Total Creatives",
      icon: <TotalCreativesSVG />,
    },
    {
      title: "Total Business",
      icon: <TotalBusinessSVG />,
    },
    {
      title: "Subscribed Business",
      icon: <SubscribedBusinessSVG />,
    },
  ];

  useEffect(() => {
    dispatch(getSignUpCounts({ filters }));
    dispatch(getMetrics({ filters }));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(getOnlinePresence());
    const timer = setInterval(() => dispatch(getOnlinePresence()), 15000);
    return () => clearInterval(timer);
  }, [dispatch]);

  return (
    <>
      <main className={styles.dashboard}>
        <section className={styles.stats}>
          {metrics &&
            Object.values(metrics).map((stat, idx) =>
              stats[idx] ? (
                <div key={idx} className={styles.stat}>
                  <p>
                    {stats[idx].icon}
                    <span>{stats[idx].title}</span>
                  </p>
                  <h2>{stat?.total ?? 0}</h2>
                </div>
              ) : null
            )}
          <div
            onClick={() => setFilterModal((prev) => !prev)}
            className={combineClasses(styles.stat, styles.filterBtn)}
          >
            <FilterIconSVG />
            <span>Filter</span>
          </div>
        </section>
        <section className={styles.onlineNow}>
          <div className={styles.onlineHeader}>
            <div>
              <h2>Online now</h2>
              <p>Users active in the last 2 minutes</p>
            </div>
            <div className={styles.onlineCount}>
              <span className={styles.onlineDot} />
              {onlinePresence?.count ?? 0}
            </div>
          </div>
          {!onlinePresence?.users?.length ? (
            <p className={styles.onlineEmpty}>No one is online right now.</p>
          ) : (
            <ul className={styles.onlineList}>
              {onlinePresence.users.map((user) => {
                const isCreator =
                  user.userType === "CREATOR" || user.userType === 0;
                const name = isCreator
                  ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                  : user.companyName ||
                    `${user.firstName || ""} ${user.lastName || ""}`.trim();
                const avatar = user.profileUrl
                  ? serverFiles(user.profileUrl)
                  : null;
                return (
                  <li key={user.id} className={styles.onlineRow}>
                    <div className={styles.onlineAvatarWrap}>
                      {avatar ? (
                        <img src={avatar} alt="" />
                      ) : (
                        <span>{(name || "?").charAt(0).toUpperCase()}</span>
                      )}
                      <span className={styles.onlineDot} />
                    </div>
                    <div className={styles.onlineMeta}>
                      <strong>{name || user.email || "Unknown"}</strong>
                      <span>
                        {typeof user.userType === "string"
                          ? user.userType
                          : "User"}
                        {user.userAliasId ? ` · @${user.userAliasId}` : ""}
                        {user.email ? ` · ${user.email}` : ""}
                      </span>
                    </div>
                    <em>
                      {formatLastSeen(user.lastSeenAt)?.replace(
                        "Last seen ",
                        ""
                      ) || "just now"}
                    </em>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section className={styles.charts}>
          <div className={styles.grossVolume}>
            <div className={styles.title}>
              <h2>Users by month</h2>
              <span>Year {year}</span>
            </div>
            <GrossVolumeChart />
          </div>
        </section>
      </main>
      {filterModal && <Filter setFilterModal={setFilterModal} />}
    </>
  );
};

export default Dashboard;
